import { useEffect } from "react";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { AavoFormLayout } from "src/components/common/forms/AavoFormLayout.tsx";
import { logError } from "src/errorHandling/errorLogging.ts";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import i18n from "i18next";
import {
	PartWarehouseLocationFormContent,
	PartWarehouseLocationFormValues,
} from "src/components/views/erp/parts/siteParts/partWarehouseLocations/PartWarehouseLocationFormContent.tsx";
import {
	PartCreationWizardApi_FormInitData
} from "src/api/generated/erp/parts/catalogPart/api/partCreationWizardApi.ts";

interface PartWarehouseLocationStepProps {
	createPartWarehouseLocation: boolean;
	onUpdateCreatePartWarehouseLocation: (createPartWarehouseLocation: boolean) => void;
	partWarehouseLocation: Partial<PartWarehouseLocationFormValues>;
	onUpdatePartWarehouseLocation: (updates: Partial<PartWarehouseLocationFormValues>) => void;
	initData: PartCreationWizardApi_FormInitData;
	onValidationChange?: (isValid: boolean) => void;
}

export const PartWarehouseLocationStep = ({
	createPartWarehouseLocation,
	onUpdateCreatePartWarehouseLocation,
	partWarehouseLocation: currentPartWarehouseLocation,
	onUpdatePartWarehouseLocation,
	initData,
	onValidationChange,
}: PartWarehouseLocationStepProps) => {
	// Create default values compatible with PartWarehouseLocation type
	const defaultValues: Partial<PartWarehouseLocationFormValues> = {
		warehouseLocationId: undefined,
		locationQuantity: 1,
		defaultReplenishmentQuantity: 1,
		isDefault: true,
		askTransferInfo: false,
		askQuantity: false,
		autoCompleteWarehouseTransfer: false,
		inventoryLevelEnabled: initData.inventoryLevelEnabledByDefault ?? false,
		givingSiteId: initData.siteId,
		replenishmentMethod: null,
		replenishmentTrigger: "MANUAL" as const,
		...currentPartWarehouseLocation,
	};

	const useAavoFormReturn = useAavoForm<PartWarehouseLocationFormValues, void>({
		defaultValues: defaultValues,
		onSubmit: () => {},
	});
	const { watch, formState, trigger } = useAavoFormReturn;

	// Update values on first render
	useEffect(() => {
		onUpdatePartWarehouseLocation(defaultValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const subscription = watch((newValues) => {
			onUpdatePartWarehouseLocation(newValues);
		});
		return () => subscription.unsubscribe();
	}, [watch, onUpdatePartWarehouseLocation]);

	useEffect(() => {
		// If createPartWarehouseLocation is false, always report as valid
		const isStepValid = createPartWarehouseLocation ? formState.isValid : true;
		onValidationChange?.(isStepValid);
	}, [formState.isValid, formState.errors, onValidationChange, createPartWarehouseLocation]);

	useEffect(() => {
		trigger().catch(logError);
	}, [trigger, createPartWarehouseLocation]);

	return (
		<AavoFormLayout columns={2}>
			<AavoCheckbox
				label={i18n.t("create_warehouse_location")}
				checked={createPartWarehouseLocation}
				onChange={(checked) => onUpdateCreatePartWarehouseLocation(checked)}
				sx={{ gridColumn: "1/-1" }}
			/>
			{createPartWarehouseLocation && (
				<PartWarehouseLocationFormContent
					partId={undefined}
					partWarehouseLocationId={undefined}
					initData={initData}
					{...useAavoFormReturn}
				/>
			)}
		</AavoFormLayout>
	);
};
